/**
 * Shared PDF Engine — core capture & assembly logic.
 *
 * Every page in the app MUST generate PDFs through this module.
 * Page-specific html2canvas / jsPDF code is not allowed.
 *
 * Pipeline: DOM page node (A4 sized) -> html2canvas -> jsPDF page.
 * Pagination of table rows is computed by `calculatePdfPagination`
 * (dynamic measurement — no fixed rowsPerPage / magic numbers).
 */
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

/**
 * A4 geometry at 96 DPI (CSS pixels).
 * 210mm x 297mm  =>  794px x 1123px.
 * These are physical paper constants, not magic numbers.
 */
export const A4 = {
  WIDTH_PX: 794,
  HEIGHT_PX: 1123,
  // Inner frame height used by the standard page frame (outer padding 40px,
  // double border 5px + 2px, matching the legacy layout used app-wide).
  INNER_HEIGHT_PX: 1033,
};

export const PDF_FONT_FAMILY =
  '"Times New Roman Bold", "Times New Roman", "Times", serif';

/** Wait for web fonts + a paint so measurements/captures are accurate. */
export const waitForRender = async (extraDelayMs = 300) => {
  await new Promise(resolve => requestAnimationFrame(() => resolve()));
  if (document.fonts && document.fonts.ready) {
    try {
      await document.fonts.ready;
    } catch {
      /* ignore */
    }
  }
  if (extraDelayMs > 0) {
    await new Promise(resolve => setTimeout(resolve, extraDelayMs));
  }
};

/**
 * Apply ONLY non-layout rendering hints inside the html2canvas clone.
 *
 * IMPORTANT: never force fontFamily/fontWeight here. Bold text is wider,
 * so forcing it at capture time makes text wrap differently in the OUTPUT
 * than in the live DOM that the pagination measured — rows grow taller and
 * collide with the footer. Templates define their own fonts; capture must
 * be WYSIWYG so that measure === preview === print.
 */
const applyCloneStyles = clonedElement => {
  if (!clonedElement) return;
  clonedElement.style.setProperty('-webkit-font-smoothing', 'antialiased');
  clonedElement.style.setProperty('-moz-osx-font-smoothing', 'grayscale');
  clonedElement.style.textRendering = 'optimizeLegibility';
  clonedElement.querySelectorAll('*').forEach(el => {
    if (el.style) {
      el.style.setProperty('-webkit-font-smoothing', 'antialiased');
      el.style.setProperty('-moz-osx-font-smoothing', 'grayscale');
      el.style.textRendering = 'optimizeLegibility';
    }
  });
};

/**
 * Capture a DOM element to canvas with the standard, app-wide settings.
 * @param {HTMLElement} element
 * @param {{ isRTL?: boolean, scale?: number }} options
 */
export const captureElement = async (element, { isRTL = true, scale } = {}) => {
  const devicePixelRatio = window.devicePixelRatio || 1;
  const effectiveScale = scale || Math.max(2, devicePixelRatio);
  return html2canvas(element, {
    scale: effectiveScale,
    useCORS: true,
    logging: false,
    backgroundColor: '#ffffff',
    letterRendering: true,
    allowTaint: false,
    removeContainer: false,
    imageTimeout: 15000,
    onclone: clonedDoc => {
      const cloned = clonedDoc.getElementById(element.id);
      applyCloneStyles(cloned || clonedDoc.body);
    },
  });
};

/**
 * Build a jsPDF document from a list of already-captured canvases,
 * one canvas per A4 page.
 */
export const buildPdfFromCanvases = canvases => {
  const pdf = new jsPDF('p', 'mm', 'a4');
  const pdfWidth = pdf.internal.pageSize.getWidth();
  const pdfHeight = pdf.internal.pageSize.getHeight();

  canvases.forEach((canvas, index) => {
    if (index > 0) pdf.addPage();
    // JPEG: pages are opaque white — no transparency needed. Encoding is
    // several times faster than PNG and the output file is much smaller,
    // which matters for large multi-page reports.
    const imgData = canvas.toDataURL('image/jpeg', 0.92);
    const imgHeight = (canvas.height * pdfWidth) / canvas.width;
    // Each captured node is A4-proportioned; clamp to page height to be safe.
    pdf.addImage(
      imgData,
      'JPEG',
      0,
      0,
      pdfWidth,
      Math.min(imgHeight, pdfHeight),
      undefined,
      'FAST',
    );
  });

  return pdf;
};

/**
 * Capture a list of DOM elements (by id) and save them as a multi-page PDF.
 * @param {{ elementIds: string[], fileName: string, isRTL?: boolean, scale?: number, onProgress?: (done:number,total:number)=>void }} params
 */
export const generatePdfFromElementIds = async ({
  elementIds,
  fileName,
  isRTL = true,
  scale,
  onProgress,
}) => {
  const canvases = [];
  // Adaptive quality: with many pages, a slightly lower capture scale keeps
  // generation time and memory bounded without visible quality loss.
  const devicePixelRatio = window.devicePixelRatio || 1;
  const effectiveScale =
    scale || (elementIds.length > 8 ? 1.5 : Math.max(2, devicePixelRatio));
  for (let i = 0; i < elementIds.length; i += 1) {
    const element = document.getElementById(elementIds[i]);
    if (!element) {
      throw new Error(`PDF engine: element #${elementIds[i]} not found`);
    }
    // eslint-disable-next-line no-await-in-loop
    const canvas = await captureElement(element, { isRTL, scale: effectiveScale });
    canvases.push(canvas);
    if (onProgress) onProgress(i + 1, elementIds.length);
  }
  const pdf = buildPdfFromCanvases(canvases);
  pdf.save(fileName);
};

/**
 * Capture a single DOM element (possibly taller than one page) and save it,
 * slicing it across pages when needed. Used for single-record PDFs.
 */
export const generateSingleElementPdf = async ({
  elementId,
  fileName,
  isRTL = true,
  scale,
}) => {
  const element = document.getElementById(elementId);
  if (!element) {
    throw new Error(`PDF engine: element #${elementId} not found`);
  }
  const devicePixelRatio = window.devicePixelRatio || 1;
  const canvas = await captureElement(element, {
    isRTL,
    scale: scale || Math.max(3, devicePixelRatio * 2),
  });

  const pdf = new jsPDF('p', 'mm', 'a4');
  const pdfWidth = pdf.internal.pageSize.getWidth();
  const pdfHeight = pdf.internal.pageSize.getHeight();
  const imgData = canvas.toDataURL('image/png', 1.0);
  const imgHeight = (canvas.height * pdfWidth) / canvas.width;

  if (imgHeight <= pdfHeight) {
    pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, imgHeight, undefined, 'FAST');
  } else {
    let position = 0;
    while (position < imgHeight) {
      if (position > 0) pdf.addPage();
      pdf.addImage(
        imgData,
        'PNG',
        0,
        -position,
        pdfWidth,
        imgHeight,
        undefined,
        'FAST',
      );
      position += pdfHeight;
    }
  }
  pdf.save(fileName);
};

/** Standard dated file name: `${prefix}_${YYYY-MM-DD}.pdf` */
export const datedFileName = (prefix, extension = 'pdf') =>
  `${prefix}_${new Date().toISOString().split('T')[0]}.${extension}`;
